// src/pages/ManagedAuthorsPage.jsx
// Phase P2: Publisher Managed Authors List Page with Delete functionality
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PublisherNav from '../components/PublisherNav';
import { Users, Plus, User, Archive, CheckCircle, Trash2, AlertTriangle, BookOpen, ChevronRight, Upload } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL as backendUrl } from '@/config/api';

const ManagedAuthorsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authors, setAuthors] = useState([]);
  const [imprintLogoUrl, setImprintLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [authorToDelete, setAuthorToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/authors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthors(data.authors || []);
        setImprintLogoUrl(data.imprint_logo_url);
      }
    } catch (error) {
      console.error('Error fetching authors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get headshot URL with fallback to imprint logo
  const getHeadshotUrl = (author) => {
    if (author.headshot_image_url) {
      return `${backendUrl}${author.headshot_image_url}?t=${Date.now()}`;
    }
    if (author.headshot_fallback_url || imprintLogoUrl) {
      return `${backendUrl}${author.headshot_fallback_url || imprintLogoUrl}?t=${Date.now()}`;
    }
    return null;
  };

  // Delete handlers
  const handleDeleteClick = (author) => {
    setAuthorToDelete(author);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!authorToDelete) return;
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/authors/${authorToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Author deleted successfully');
        setAuthors(prev => prev.filter(a => a.id !== authorToDelete.id));
        setDeleteModalOpen(false);
        setAuthorToDelete(null);
      } else {
        const error = await response.json();
        // Check if it's a "has titles" error - show special message
        if (error.detail && error.detail.includes('title')) {
          toast.error(error.detail, { duration: 6000 });
        } else {
          toast.error(error.detail || 'Failed to delete author');
        }
      }
    } catch (error) {
      console.error('Error deleting author:', error);
      toast.error('Failed to delete author');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-6xl px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="rounded-full bg-blue-600/20 p-2">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold">Managed Authors</h1>
            </div>
            <p className="text-slate-400">
              Authors form the foundation of your publishing catalog. Manage your author roster here.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/publisher')}
              className="inline-flex items-center gap-2 rounded-full bg-slate-700 hover:bg-slate-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              Publisher Home
            </button>
            <button
              onClick={() => navigate('/publisher/authors/new')}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Author
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
            <p className="mt-4 text-slate-400">Loading authors...</p>
          </div>
        ) : authors.length === 0 ? (
          /* Empty State */
          <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mb-6">
              <Users className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-200 mb-2">No Authors Yet</h2>
            <p className="text-slate-400 max-w-md mx-auto mb-6">
              Authors are the foundation of your publishing catalog. Create an author to begin organizing titles.
            </p>
            <button
              onClick={() => navigate('/publisher/authors/new')}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Your First Author
            </button>
          </div>
        ) : (
          /* Authors Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {authors.map((author) => {
              const headshotUrl = getHeadshotUrl(author);
              const isArchived = author.status === 'archived';
              
              return (
                <div
                  key={author.id}
                  className={`rounded-2xl border bg-slate-900/50 p-7 transition-colors ${
                    isArchived 
                      ? 'border-slate-700/50 opacity-60' 
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-6">
                    {/* Headshot / Fallback - 25% larger */}
                    <div className="flex-shrink-0">
                      {headshotUrl ? (
                        <img
                          src={headshotUrl}
                          alt={author.pen_name}
                          className="w-24 h-24 rounded-xl object-cover border border-slate-600"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-xl bg-slate-800 border border-slate-600 flex items-center justify-center">
                          <User className="w-12 h-12 text-slate-500" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-xl text-slate-100 truncate">
                        {author.pen_name}
                      </h3>
                      
                      {/* Title Count */}
                      <div className="mt-1.5">
                        <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
                          <BookOpen className="w-4 h-4 text-slate-400" />
                          {author.title_count || 0} {author.title_count === 1 ? 'Title' : 'Titles'}
                        </span>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="mt-2">
                        {isArchived ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                            <Archive className="w-4 h-4" />
                            Archived
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-5 flex flex-col gap-3">
                    {/* Primary Action - Author Console */}
                    <Link
                      to={`/publisher/authors/${author.id}/console`}
                      className="flex items-center justify-between rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 px-5 py-3 text-base text-indigo-300 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Upload className="w-5 h-5" />
                        Author Console
                      </span>
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                    
                    {/* Delete Action */}
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleDeleteClick(author)}
                        className="flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-red-900/50 hover:text-red-400 px-4 py-2.5 text-sm text-slate-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Author
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong className="text-slate-200">{authorToDelete?.pen_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4 p-4 rounded-lg bg-amber-900/20 border border-amber-500/30">
            <p className="text-sm text-amber-300">
              <strong>Note:</strong> Authors with titles cannot be deleted. If this author has any titles (especially with accrued EUs), the delete will fail and you will need to archive the author instead.
            </p>
          </div>
          
          <DialogFooter className="gap-3">
            <button
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleting}
              className="px-5 py-2 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold transition-colors disabled:opacity-50"
            >
              {deleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete Author
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UniversalFooter />
    </div>
  );
};

export default ManagedAuthorsPage;
